(() => {
  const GLOBAL_KEY = 'FlextailSimpleProductCompare';
  const SECTION_SELECTOR = '[data-section-type="simple-product-compare"]';

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].initAll === 'function') {
    window[GLOBAL_KEY].initAll();
    return;
  }

  const instances = new WeakMap();

  class SimpleProductCompare {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId || Math.random().toString(36).slice(2);
      this.dataElement = section.querySelector('[data-simple-product-compare-data]');
      this.tabsContainer = section.querySelector('[data-simple-product-compare-tabs]');
      this.grid = section.querySelector('[data-simple-product-compare-grid]');
      this.mediaQuery = window.matchMedia('(max-width: 740px)');
      this.stickyHeaderFrame = null;
      this.ready = false;

      if (!this.dataElement || !this.grid) {
        return;
      }

      try {
        this.data = JSON.parse(this.dataElement.textContent);
      } catch (error) {
        console.warn('Simple product compare data could not be parsed.', error);
        return;
      }

      this.products = (this.data.products || []).map((product) => this.normalizeProduct(product));
      this.tabs = (this.data.tabs || []).map((tab) => ({
        ...tab,
        id: String(tab.id),
        collectionHandle: String(tab.collectionHandle || ''),
      }));
      this.rows = this.data.rows || [];
      this.currentProductId = this.data.currentProductId ? String(this.data.currentProductId) : '';
      this.currentProductCollectionHandles = (this.data.currentProductCollectionHandles || []).map((handle) => String(handle));
      this.currentProduct = this.data.currentProduct ? this.normalizeProduct(this.data.currentProduct) : null;
      this.scopeToCurrentProductCategory = Boolean(this.data.scopeToCurrentProductCategory && this.currentProductId);
      this.ensureCurrentProductAvailable();
      this.desktopCount = Math.max(1, Math.min(Number(this.data.desktopColumns) || 1, this.products.length));
      this.mobileCount = Math.max(1, Math.min(2, this.products.length));
      this.isMobile = this.mediaQuery.matches;
      this.currentProductMatch = this.findCurrentProductMatch();

      if (this.scopeToCurrentProductCategory && !this.currentProductMatch && !this.data.designMode) {
        this.section.hidden = true;
        this.ready = true;
        return;
      }

      this.activeTabId = this.initialTabId();
      this.desktopSelected = this.initialSelection(this.desktopCount);
      this.mobileSelected = this.fillSelection(this.desktopSelected.slice(0, this.mobileCount), this.mobileCount);

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleModeChange = this.handleModeChange.bind(this);
      this.scheduleStickyHeaderStateUpdate = this.scheduleStickyHeaderStateUpdate.bind(this);
      this.updateStickyHeaderState = this.updateStickyHeaderState.bind(this);

      this.section.dataset.simpleProductCompareReady = 'true';
      this.section.addEventListener('click', this.handleClick);
      this.section.addEventListener('keydown', this.handleKeydown);
      this.section.addEventListener('load', this.scheduleStickyHeaderStateUpdate, true);
      document.addEventListener('click', this.handleDocumentClick);
      window.addEventListener('scroll', this.scheduleStickyHeaderStateUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleStickyHeaderStateUpdate);

      if (typeof this.mediaQuery.addEventListener === 'function') {
        this.mediaQuery.addEventListener('change', this.handleModeChange);
      } else if (typeof this.mediaQuery.addListener === 'function') {
        this.mediaQuery.addListener(this.handleModeChange);
      }

      this.ready = true;
      this.render();
    }

    destroy() {
      this.section.dataset.simpleProductCompareReady = 'false';
      this.section.removeEventListener('click', this.handleClick);
      this.section.removeEventListener('keydown', this.handleKeydown);
      this.section.removeEventListener('load', this.scheduleStickyHeaderStateUpdate, true);
      document.removeEventListener('click', this.handleDocumentClick);
      window.removeEventListener('scroll', this.scheduleStickyHeaderStateUpdate);
      window.removeEventListener('resize', this.scheduleStickyHeaderStateUpdate);

      if (this.stickyHeaderFrame !== null) {
        window.cancelAnimationFrame(this.stickyHeaderFrame);
        this.stickyHeaderFrame = null;
      }

      if (typeof this.mediaQuery.removeEventListener === 'function') {
        this.mediaQuery.removeEventListener('change', this.handleModeChange);
      } else if (typeof this.mediaQuery.removeListener === 'function') {
        this.mediaQuery.removeListener(this.handleModeChange);
      }
    }

    hasTabs() {
      return this.tabs.length > 1;
    }

    normalizeProduct(product) {
      return {
        ...product,
        id: String(product.id),
        productId: String(product.productId || product.id),
        tabId: String(product.tabId || ''),
        position: product.position == null ? null : Number(product.position),
        isCurrentProduct: Boolean(product.isCurrentProduct),
      };
    }

    productBelongsToCollectionHandle(handle) {
      return Boolean(handle && this.currentProductCollectionHandles.includes(handle));
    }

    currentProductTab() {
      if (!this.currentProductId) {
        return null;
      }

      return this.tabs.find((tab) => this.productBelongsToCollectionHandle(tab.collectionHandle)) || null;
    }

    currentProductMatchesFallbackCollection() {
      return this.productBelongsToCollectionHandle(String(this.data.compareCollectionHandle || ''));
    }

    ensureCurrentProductAvailable() {
      if (!this.scopeToCurrentProductCategory || !this.currentProduct) {
        return;
      }

      const matchedTab = this.currentProductTab();
      const currentProductAlreadyAvailable = this.products.some((product) => {
        if (product.productId !== this.currentProductId) {
          return false;
        }

        return matchedTab ? product.tabId === matchedTab.id : true;
      });

      if (currentProductAlreadyAvailable) {
        return;
      }

      if (!matchedTab && !this.currentProductMatchesFallbackCollection()) {
        return;
      }

      const currentProduct = {
        ...this.currentProduct,
        id: matchedTab ? `${matchedTab.id}-${this.currentProductId}-current` : this.currentProduct.id,
        tabId: matchedTab ? matchedTab.id : '',
        position: null,
        isCurrentProduct: true,
      };

      const tabInsertIndex = matchedTab
        ? this.products.findIndex((product) => product.tabId === matchedTab.id)
        : 0;
      const insertIndex = tabInsertIndex >= 0 ? tabInsertIndex : this.products.length;

      this.products.splice(insertIndex, 0, currentProduct);
    }

    findCurrentProductMatch() {
      if (!this.currentProductId) {
        return null;
      }

      const matchedTab = this.currentProductTab();

      if (matchedTab) {
        return this.products.find((product) => product.productId === this.currentProductId && product.tabId === matchedTab.id) || null;
      }

      return this.products.find((product) => product.productId === this.currentProductId) || null;
    }

    initialTabId() {
      if (!this.hasTabs()) {
        return null;
      }

      if (this.currentProductMatch && this.currentProductMatch.tabId) {
        return this.currentProductMatch.tabId;
      }

      return this.tabs[0].id;
    }

    displayTabs() {
      if (!this.hasTabs()) {
        return [];
      }

      if (this.scopeToCurrentProductCategory && this.activeTabId) {
        const activeTab = this.currentTab();
        return activeTab ? [activeTab] : [];
      }

      return this.tabs;
    }

    initialSelection(count) {
      const products = this.currentProducts();
      const selection = [];
      const currentProduct = this.currentProductId
        ? products.find((product) => product.productId === this.currentProductId)
        : null;

      if (currentProduct) {
        selection.push(currentProduct.id);
      }

      products.forEach((product) => {
        if (selection.length < count && !selection.includes(product.id)) {
          selection.push(product.id);
        }
      });

      return this.fillSelection(selection, count, products);
    }

    productsForTab(tab) {
      if (!tab) {
        return this.products;
      }

      return this.products.filter((product) => product.tabId === tab.id);
    }

    currentTab() {
      if (!this.hasTabs()) {
        return null;
      }

      const activeTab = this.tabs.find((tab) => tab.id === this.activeTabId);

      return activeTab || this.tabs[0];
    }

    currentProducts() {
      const tab = this.currentTab();

      if (!tab) {
        return this.products;
      }

      return this.productsForTab(tab);
    }

    fillSelection(selection, count, products = this.currentProducts()) {
      const validIds = products.map((product) => product.id);
      const uniqueSelection = [];

      selection.forEach((id) => {
        const normalizedId = String(id);

        if (validIds.includes(normalizedId) && !uniqueSelection.includes(normalizedId) && uniqueSelection.length < count) {
          uniqueSelection.push(normalizedId);
        }
      });

      products.forEach((product) => {
        if (uniqueSelection.length < count && !uniqueSelection.includes(product.id)) {
          uniqueSelection.push(product.id);
        }
      });

      return uniqueSelection;
    }

    selectedIds() {
      return this.isMobile ? this.mobileSelected : this.desktopSelected;
    }

    setSelectedIds(nextSelection) {
      if (this.isMobile) {
        this.mobileSelected = this.fillSelection(nextSelection, this.mobileCount);
      } else {
        this.desktopSelected = this.fillSelection(nextSelection, this.desktopCount);
      }
    }

    productsForSelection(selection) {
      const products = this.currentProducts();

      return selection
        .map((id) => products.find((product) => product.id === id))
        .filter(Boolean);
    }

    escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    stripHtml(value) {
      const holder = document.createElement('div');
      holder.innerHTML = value || '';
      return holder.textContent.trim();
    }

    formatLineBreaks(value) {
      const html = String(value == null ? '' : value);

      if (!html.includes('\\n') && !html.includes('\n')) {
        return html;
      }

      const template = document.createElement('template');
      template.innerHTML = html;

      const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
      const textNodes = [];

      while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
      }

      textNodes.forEach((textNode) => {
        const parts = textNode.nodeValue.split(/\\n|\n/);

        if (parts.length === 1) {
          return;
        }

        const fragment = document.createDocumentFragment();

        parts.forEach((part, index) => {
          if (index > 0) {
            fragment.appendChild(document.createElement('br'));
          }

          fragment.appendChild(document.createTextNode(part));
        });

        textNode.replaceWith(fragment);
      });

      return template.innerHTML;
    }

    iconChevronDown() {
      return '<svg class="simple-product-compare__selector-icon" aria-hidden="true" focusable="false" viewBox="0 0 16 16"><path d="M3.4 5.8 8 10.4l4.6-4.6 1.1 1.1L8 12.6 2.3 6.9l1.1-1.1Z" fill="currentColor"></path></svg>';
    }

    iconArrowRight() {
      return '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16"><path d="M9.2 3.2 14 8l-4.8 4.8-1-1L11.3 8 8.2 4.2l1-1ZM2 7.3h10.8v1.4H2V7.3Z" fill="currentColor"></path></svg>';
    }

    iconProductPlaceholder() {
      return '<svg aria-hidden="true" focusable="false" viewBox="0 0 64 64"><path d="M16 18h32v28H16V18Zm4 4v20h24V22H20Zm4 24h16v4H24v-4Zm6-18h4v8h-4v-8Z" fill="currentColor"></path></svg>';
    }

    iconBoolean(included) {
      const label = included ? 'Included' : 'Not included';
      const className = included ? 'simple-product-compare__boolean--yes' : 'simple-product-compare__boolean--no';
      const path = included
        ? 'M6.2 11.4 2.8 8l1.4-1.4 2 2 5.6-5.6 1.4 1.4-7 7Z'
        : 'm4.2 3 3.8 3.8L11.8 3 13 4.2 9.2 8l3.8 3.8-1.2 1.2L8 9.2 4.2 13 3 11.8 6.8 8 3 4.2 4.2 3Z';

      return `
        <span class="simple-product-compare__boolean ${className}" aria-label="${label}">
          <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16">
            <path d="${path}" fill="currentColor"></path>
          </svg>
        </span>
      `;
    }

    renderSelector(product, columnIndex, selection, options = {}) {
      const selectedByOtherColumn = selection.filter((id, index) => index !== columnIndex);
      const selectableOptions = this.currentProducts().filter((option) => !selectedByOtherColumn.includes(option.id) && option.id !== product.id);

      if (selectableOptions.length === 0) {
        return '';
      }

      const idSuffix = options.idSuffix ? `-${options.idSuffix}` : '';
      const menuId = `simple-product-compare-menu-${this.sectionId}-${columnIndex}${idSuffix}`;
      const extraClass = options.className ? ` ${options.className}` : '';
      const extraAttributes = options.attributes ? ` ${options.attributes}` : '';

      return `
        <div class="simple-product-compare__selector${extraClass}" data-simple-product-compare-selector data-column-index="${columnIndex}"${extraAttributes}>
          <button
            type="button"
            class="simple-product-compare__selector-trigger"
            aria-expanded="false"
            aria-controls="${menuId}"
            aria-haspopup="listbox"
          >
            <span class="simple-product-compare__selector-label">${this.escapeHtml(product.title)}</span>
            ${this.iconChevronDown()}
          </button>
          <div class="simple-product-compare__selector-menu" id="${menuId}" role="listbox" hidden>
            ${selectableOptions.map((option) => `
                <button
                  type="button"
                  class="simple-product-compare__selector-option"
                  role="option"
                  data-product-id="${this.escapeHtml(option.id)}"
                  aria-selected="false"
                >
                  ${this.escapeHtml(option.title)}
                </button>
            `).join('')}
          </div>
        </div>
      `;
    }

    renderProductImage(product) {
      return product.image && product.image.src
        ? `<img class="simple-product-compare__image" src="${this.escapeHtml(product.image.src)}" srcset="${this.escapeHtml(product.image.srcset)}" sizes="(min-width: 1000px) 230px, 50vw" alt="${this.escapeHtml(product.image.alt || product.title)}" loading="lazy">`
        : `<span class="simple-product-compare__placeholder">${this.iconProductPlaceholder()}</span>`;
    }

    renderPrimaryAction(product, options = {}) {
      if (!this.data.primaryCtaLabel || !product.primaryUrl) {
        return '';
      }

      const extraClass = options.className ? ` ${options.className}` : '';
      const extraAttributes = options.attributes ? ` ${options.attributes}` : '';

      return `
        <div class="simple-product-compare__actions${extraClass}"${extraAttributes}>
          <a class="simple-product-compare__primary-link" href="${this.escapeHtml(product.primaryUrl)}">${this.escapeHtml(this.data.primaryCtaLabel)}</a>
        </div>
      `;
    }

    renderTabs() {
      if (!this.tabsContainer) {
        return;
      }

      const tabs = this.displayTabs();

      if (tabs.length <= 1) {
        this.tabsContainer.innerHTML = '';
        return;
      }

      const activeTab = this.currentTab();

      this.tabsContainer.innerHTML = `
        <div class="simple-product-compare__tab-wrapper" aria-label="Product categories">
          ${tabs.map((tab, index) => `
            ${index > 0 ? '<span class="simple-product-compare__tab-spacer" aria-hidden="true"></span>' : ''}
            <button
              type="button"
              class="simple-product-compare__tab${activeTab && tab.id === activeTab.id ? ' is-active' : ''}"
              aria-pressed="${activeTab && tab.id === activeTab.id ? 'true' : 'false'}"
              data-simple-product-compare-tab="${this.escapeHtml(tab.id)}"
            >
              ${this.escapeHtml(tab.label)}
            </button>
          `).join('')}
        </div>
      `;
    }

    setActiveTab(tabId) {
      if (!this.hasTabs() || this.scopeToCurrentProductCategory || this.activeTabId === tabId) {
        return;
      }

      const nextTab = this.tabs.find((tab) => tab.id === tabId);

      if (!nextTab) {
        return;
      }

      this.activeTabId = nextTab.id;
      this.desktopSelected = this.fillSelection([], this.desktopCount);
      this.mobileSelected = this.fillSelection(this.desktopSelected.slice(0, this.mobileCount), this.mobileCount);
      this.closeSelectors();
      this.render();
    }

    renderProduct(product, columnIndex, selection) {
      const imageHtml = this.renderProductImage(product);
      const primaryLink = this.data.primaryCtaLabel && product.primaryUrl
        ? `<a class="simple-product-compare__primary-link" href="${this.escapeHtml(product.primaryUrl)}">${this.escapeHtml(this.data.primaryCtaLabel)}</a>`
        : '';
      const secondaryLink = this.data.secondaryCtaLabel && product.secondaryUrl
        ? `<a class="simple-product-compare__secondary-link" href="${this.escapeHtml(product.secondaryUrl)}">${this.escapeHtml(this.data.secondaryCtaLabel)} ${this.iconArrowRight()}</a>`
        : '';
      const actions = primaryLink || secondaryLink
        ? `<div class="simple-product-compare__actions">${primaryLink}${secondaryLink}</div>`
        : '';

      return `
        <div class="simple-product-compare__cell simple-product-compare__cell--header" role="columnheader">
          <div class="simple-product-compare__product">
            ${this.renderSelector(product, columnIndex, selection)}
            <div class="simple-product-compare__product-presentation">
              <div class="simple-product-compare__media">${imageHtml}</div>
              <h3 class="simple-product-compare__product-title">${this.escapeHtml(product.title)}</h3>
              ${actions}
            </div>
          </div>
        </div>
      `;
    }

    renderMobileProduct(product, columnIndex, selection) {
      const selectorHtml = this.renderSelector(product, columnIndex, selection, {
        idSuffix: 'mobile-source',
        attributes: 'data-mobile-sticky-source="selector"',
      });
      const staticTitle = selectorHtml
        ? ''
        : `<h3 class="simple-product-compare__product-title">${this.escapeHtml(product.title)}</h3>`;
      const imageHtml = this.renderProductImage(product);
      const actions = this.renderPrimaryAction(product, {
        attributes: `data-mobile-sticky-source="actions" data-column-index="${columnIndex}"`,
      });

      return `
        <div class="simple-product-compare__cell simple-product-compare__cell--header" role="columnheader">
          <div class="simple-product-compare__product simple-product-compare__product--mobile-header">
            ${selectorHtml}
            <div class="simple-product-compare__product-presentation">
              ${staticTitle}
              <div class="simple-product-compare__media simple-product-compare__media--mobile-header">${imageHtml}</div>
              ${actions}
            </div>
          </div>
        </div>
      `;
    }

    renderMobileStickyProduct(product, columnIndex, selection) {
      const selectorHtml = this.renderSelector(product, columnIndex, selection, {
        idSuffix: 'mobile-sticky',
        className: 'simple-product-compare__sticky-part',
        attributes: 'data-mobile-sticky-part="selector"',
      });
      const actions = this.renderPrimaryAction(product, {
        className: 'simple-product-compare__sticky-part simple-product-compare__sticky-part--actions',
        attributes: `data-mobile-sticky-part="actions" data-column-index="${columnIndex}"`,
      });

      return `
        <div class="simple-product-compare__cell simple-product-compare__cell--header" role="columnheader">
          <div class="simple-product-compare__product simple-product-compare__product--mobile-sticky">
            ${selectorHtml}
            ${actions}
          </div>
        </div>
      `;
    }

    renderValue(row, product) {
      const value = this.valueForProduct(row, product);

      if (row.valueType === 'yes_no') {
        return this.iconBoolean(Boolean(value && value.enabled));
      }

      const html = value && value.html ? value.html : '';

      if (this.stripHtml(html).length > 0) {
        return `<div class="rte">${this.formatLineBreaks(html)}</div>`;
      }

      return `<span class="simple-product-compare__empty">${this.escapeHtml(this.data.emptyValueText || '-')}</span>`;
    }

    valueForProduct(row, product) {
      if (product.isCurrentProduct && row.currentProductValue) {
        return row.currentProductValue;
      }

      return row.values && product.position != null && row.values[product.position] ? row.values[product.position] : null;
    }

    rowHasVisibleValue(row, visibleProducts) {
      if (!row.hideWhenEmpty) {
        return true;
      }

      return visibleProducts.some((product) => {
        const value = this.valueForProduct(row, product);

        return value && this.stripHtml(value.html).length > 0;
      });
    }

    rowsForVisibleProducts(visibleProducts) {
      const visibleRows = [];
      let pendingGroup = null;

      this.rows.forEach((row) => {
        if (row.type === 'group') {
          pendingGroup = row;
          return;
        }

        if (this.rowHasVisibleValue(row, visibleProducts)) {
          if (pendingGroup) {
            visibleRows.push(pendingGroup);
            pendingGroup = null;
          }

          visibleRows.push(row);
        }
      });

      return visibleRows;
    }

    renderDesktop(selection) {
      const visibleProducts = this.productsForSelection(selection);
      const visibleRows = this.rowsForVisibleProducts(visibleProducts);

      this.grid.style.setProperty('--compare-columns', visibleProducts.length);
      this.grid.innerHTML = `
        <div class="simple-product-compare__row simple-product-compare__row--header" role="row">
          <div class="simple-product-compare__cell simple-product-compare__cell--corner" role="columnheader" aria-label="Specifications"></div>
          ${visibleProducts.map((product, index) => this.renderProduct(product, index, selection)).join('')}
        </div>
        ${visibleRows.map((row) => {
          if (row.type === 'group') {
            return `
              <div class="simple-product-compare__row simple-product-compare__row--group" role="row">
                <div class="simple-product-compare__group-cell" role="cell">
                  <h3 class="simple-product-compare__group-title">${this.escapeHtml(row.title)}</h3>
                </div>
              </div>
            `;
          }

          return `
            <div class="simple-product-compare__row simple-product-compare__row--spec" role="row">
              <div class="simple-product-compare__cell simple-product-compare__cell--label" role="rowheader">
                ${this.escapeHtml(row.label)}
              </div>
              ${visibleProducts.map((product) => `
                <div class="simple-product-compare__cell simple-product-compare__value" role="cell">
                  ${this.renderValue(row, product)}
                </div>
              `).join('')}
            </div>
          `;
        }).join('')}
      `;
    }

    renderMobile(selection) {
      const visibleProducts = this.productsForSelection(selection);
      const visibleRows = this.rowsForVisibleProducts(visibleProducts);

      this.grid.style.setProperty('--compare-columns', visibleProducts.length);
      this.grid.innerHTML = `
        <div class="simple-product-compare__row simple-product-compare__row--header simple-product-compare__row--mobile-sticky-header" role="row">
          ${visibleProducts.map((product, index) => this.renderMobileStickyProduct(product, index, selection)).join('')}
        </div>
        <div class="simple-product-compare__row simple-product-compare__row--header simple-product-compare__row--mobile-product-header" role="row">
          ${visibleProducts.map((product, index) => this.renderMobileProduct(product, index, selection)).join('')}
        </div>
        ${visibleRows.map((row) => {
          if (row.type === 'group') {
            return `
              <div class="simple-product-compare__row simple-product-compare__row--group" role="row">
                <div class="simple-product-compare__group-cell" role="cell">
                  <h3 class="simple-product-compare__group-title">${this.escapeHtml(row.title)}</h3>
                </div>
              </div>
            `;
          }

          return `
            <div class="simple-product-compare__row simple-product-compare__row--spec" role="row">
              <div class="simple-product-compare__cell simple-product-compare__cell--label" role="rowheader">
                ${this.escapeHtml(row.label)}
              </div>
              <div class="simple-product-compare__mobile-values">
                ${visibleProducts.map((product) => `
                  <div class="simple-product-compare__cell simple-product-compare__value" role="cell">
                    ${this.renderValue(row, product)}
                  </div>
                `).join('')}
              </div>
            </div>
          `;
        }).join('')}
      `;
    }

    render() {
      const selection = this.fillSelection(this.selectedIds(), this.isMobile ? this.mobileCount : this.desktopCount);

      if (this.isMobile) {
        this.mobileSelected = selection;
      } else {
        this.desktopSelected = selection;
      }

      this.renderTabs();

      if (this.isMobile) {
        this.renderMobile(selection);
      } else {
        this.renderDesktop(selection);
      }

      this.scheduleStickyHeaderStateUpdate();
    }

    updateStickyHeaderState() {
      this.stickyHeaderFrame = null;

      const stickyHeader = this.section.querySelector('.simple-product-compare__row--mobile-sticky-header');

      if (!stickyHeader || !this.isMobile) {
        if (stickyHeader) {
          stickyHeader.classList.remove('is-active');
          stickyHeader.style.removeProperty('--mobile-sticky-header-height');
          stickyHeader.querySelectorAll('[data-mobile-sticky-part]').forEach((part) => part.classList.remove('is-visible'));
        }
        return;
      }

      const stickyTop = parseFloat(window.getComputedStyle(stickyHeader).top) || 0;
      let hasVisiblePart = false;

      stickyHeader.querySelectorAll('[data-mobile-sticky-part]').forEach((part) => part.classList.remove('is-visible'));

      this.section.querySelectorAll('[data-mobile-sticky-source]').forEach((source) => {
        const sourceType = source.dataset.mobileStickySource;
        const columnIndex = source.dataset.columnIndex;

        if (!sourceType || columnIndex == null || source.getBoundingClientRect().bottom > stickyTop) {
          return;
        }

        const stickyPart = stickyHeader.querySelector(`[data-mobile-sticky-part="${sourceType}"][data-column-index="${columnIndex}"]`);

        if (stickyPart) {
          stickyPart.classList.add('is-visible');
          hasVisiblePart = true;
        }
      });

      stickyHeader.classList.toggle('is-active', hasVisiblePart);

      if (hasVisiblePart) {
        stickyHeader.style.setProperty('--mobile-sticky-header-height', '0px');
        stickyHeader.style.setProperty('--mobile-sticky-header-height', `${stickyHeader.scrollHeight}px`);
      } else {
        stickyHeader.style.removeProperty('--mobile-sticky-header-height');
      }
    }

    scheduleStickyHeaderStateUpdate() {
      if (this.stickyHeaderFrame !== null) {
        return;
      }

      this.stickyHeaderFrame = window.requestAnimationFrame(this.updateStickyHeaderState);
    }

    closeSelectors(exceptSelector) {
      this.section.querySelectorAll('[data-simple-product-compare-selector]').forEach((selector) => {
        if (selector === exceptSelector) {
          return;
        }

        selector.classList.remove('is-open');
        const trigger = selector.querySelector('.simple-product-compare__selector-trigger');
        const menu = selector.querySelector('.simple-product-compare__selector-menu');

        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        }

        if (menu) {
          menu.hidden = true;
        }
      });
    }

    toggleSelector(selector, forceOpen) {
      const trigger = selector.querySelector('.simple-product-compare__selector-trigger');
      const menu = selector.querySelector('.simple-product-compare__selector-menu');

      if (!trigger || !menu) {
        return;
      }

      const nextOpen = typeof forceOpen === 'boolean' ? forceOpen : menu.hidden;

      this.closeSelectors(selector);
      selector.classList.toggle('is-open', nextOpen);
      menu.hidden = !nextOpen;
      trigger.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');

      if (nextOpen) {
        const activeOption = menu.querySelector('[aria-selected="true"]') || menu.querySelector('.simple-product-compare__selector-option');

        if (activeOption) {
          activeOption.focus();
        }
      }
    }

    focusSiblingOption(option, direction) {
      const options = Array.from(option.closest('.simple-product-compare__selector-menu').querySelectorAll('.simple-product-compare__selector-option'));
      const currentIndex = options.indexOf(option);
      const nextIndex = (currentIndex + direction + options.length) % options.length;

      if (options[nextIndex]) {
        options[nextIndex].focus();
      }
    }

    handleClick(event) {
      const tabTrigger = event.target.closest('[data-simple-product-compare-tab]');
      const trigger = event.target.closest('.simple-product-compare__selector-trigger');
      const option = event.target.closest('.simple-product-compare__selector-option');

      if (tabTrigger) {
        this.setActiveTab(tabTrigger.dataset.simpleProductCompareTab);
        return;
      }

      if (trigger) {
        this.toggleSelector(trigger.closest('[data-simple-product-compare-selector]'));
        return;
      }

      if (option && !option.disabled) {
        const selector = option.closest('[data-simple-product-compare-selector]');
        const columnIndex = Number(selector.dataset.columnIndex);
        const selection = this.selectedIds().slice();

        selection[columnIndex] = option.dataset.productId;
        this.setSelectedIds(selection);
        this.closeSelectors();
        this.render();
      }
    }

    handleKeydown(event) {
      const trigger = event.target.closest('.simple-product-compare__selector-trigger');
      const option = event.target.closest('.simple-product-compare__selector-option');

      if (trigger && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')) {
        event.preventDefault();
        this.toggleSelector(trigger.closest('[data-simple-product-compare-selector]'), true);
        return;
      }

      if (trigger && event.key === 'Escape') {
        this.closeSelectors();
        trigger.focus();
        return;
      }

      if (option && event.key === 'Escape') {
        const selector = option.closest('[data-simple-product-compare-selector]');
        const selectorTrigger = selector.querySelector('.simple-product-compare__selector-trigger');

        this.closeSelectors();

        if (selectorTrigger) {
          selectorTrigger.focus();
        }
        return;
      }

      if (option && event.key === 'ArrowDown') {
        event.preventDefault();
        this.focusSiblingOption(option, 1);
        return;
      }

      if (option && event.key === 'ArrowUp') {
        event.preventDefault();
        this.focusSiblingOption(option, -1);
      }
    }

    handleDocumentClick(event) {
      if (!this.section.contains(event.target)) {
        this.closeSelectors();
      }
    }

    handleModeChange() {
      const nextIsMobile = this.mediaQuery.matches;

      if (nextIsMobile === this.isMobile) {
        return;
      }

      this.isMobile = nextIsMobile;

      if (nextIsMobile) {
        this.mobileSelected = this.fillSelection(this.desktopSelected.slice(0, this.mobileCount), this.mobileCount);
      } else {
        this.desktopSelected = this.fillSelection(this.desktopSelected, this.desktopCount);
      }

      this.closeSelectors();
      this.render();
    }
  }

  function initSection(section) {
    if (!section || instances.has(section)) {
      return;
    }

    const instance = new SimpleProductCompare(section);

    if (instance.ready) {
      instances.set(section, instance);
    }
  }

  function destroySection(section) {
    const instance = instances.get(section);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(section);
  }

  function initAll(root = document) {
    if (root.matches && root.matches(SECTION_SELECTOR)) {
      initSection(root);
    }

    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  window[GLOBAL_KEY] = { initAll };

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches && event.target.matches(SECTION_SELECTOR)) {
      destroySection(event.target);
    }

    event.target.querySelectorAll(SECTION_SELECTOR).forEach(destroySection);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
})();
